/* ---------------------------------------------------------------------------
   The typed fetch wrapper. ONE place unwraps the api.Response envelope.

   Every response this backend produces — success and failure alike — is
   `{status, message, error?, data?, meta?}` (openapi.yaml, convention 1). If
   each screen unwrapped that itself, the envelope's `error` code would be read
   in some places and the HTTP status in others, and the two disagree by design:
   a 409 carries a machine code in `error` and the actionable detail in `data`.
   On this surface that detail is load-bearing — a refused claim names the
   expert holding it, and a refused promotion carries the whole pre-flight with
   every blocking reason at once — so the unwrap happens here, once, and screens
   receive either data or an ApiError carrying both the code and the detail.

   Duplicated in shape from admin-dashboard/src/lib/api.ts. No shared package by
   design; the three apps stay in sync by review.
--------------------------------------------------------------------------- */
import type { Claim, ComposerReasonCode, ComposerState, Meta, PromotionPreflight } from '@/types/api';

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export interface Envelope<TData> {
  status: boolean;
  message: string;
  error?: string;
  data?: TData;
  meta?: Meta;
}

export interface Page<TItem> {
  items: TItem[];
  meta?: Meta;
}

/** A failed call. `code` is the envelope's stable machine code and is the only
    thing worth branching on; `message` is safe to display verbatim. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  is(...codes: string[]): boolean {
    return codes.includes(this.code);
  }

  /** Field-level validation detail, when the server sent any. */
  get fields(): { field: string; code: string; message?: string }[] {
    const d = this.detail as { fields?: { field: string; code: string; message?: string }[] } | undefined;
    return d?.fields ?? [];
  }

  /** The 409 from CanPrompt — the same reason code that greyed the composer. */
  get reasonCode(): ComposerReasonCode | null {
    const d = this.detail as { reason_code?: ComposerReasonCode } | undefined;
    return d?.reason_code ?? null;
  }

  get composer(): ComposerState | null {
    const d = this.detail as { composer?: ComposerState } | undefined;
    return d?.composer ?? null;
  }

  /** A lost claim race names the holder, so the UI can say who rather than "try again". */
  get claim(): Claim {
    const d = this.detail as { claim?: Claim } | undefined;
    return d?.claim ?? null;
  }

  /** A refused promotion returns the whole pre-flight: every reason at once. */
  get preflight(): PromotionPreflight | null {
    const d = this.detail as Partial<PromotionPreflight> | undefined;
    if (!d || !Array.isArray(d.repos) || typeof d.can_promote !== 'boolean') return null;
    return d as PromotionPreflight;
  }
}

// -- access token -----------------------------------------------------------
// Held in memory only. The refresh token is an HttpOnly cookie the SPA cannot
// read, so a script that reaches this module's memory gets a token that expires
// in minutes rather than a refresh family. That matters more here than on any
// other surface: this dashboard renders agent markdown produced from an
// untrusted repository, which is exactly the XSS path that would reach it.

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered once by the session provider. Called when the refresh family is
    gone and the expert has to sign in again. */
export function setUnauthenticatedHandler(handler: (() => void) | null) {
  onUnauthenticated = handler;
}

// -- refresh, single-flight -------------------------------------------------
// The review page fires several queries at once and holds an SSE stream open
// beside them. Without single-flight, an expired token produces one refresh per
// caller, and refresh rotation is a family: the second rotation looks like token
// reuse to the server, which revokes the whole family and signs the expert out
// in the middle of a review.

let refreshInFlight: Promise<boolean> | null = null;

export async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!res.ok) return false;
        const body = (await res.json()) as Envelope<{ access_token: string }>;
        if (!body.status || !body.data?.access_token) return false;
        accessToken = body.data.access_token;
        return true;
      } catch {
        return false;
      } finally {
        // Cleared on the next tick so concurrent callers all observe the same
        // resolved promise rather than starting a second rotation.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

// -- request ----------------------------------------------------------------

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip the refresh-and-retry dance. Used by the auth calls themselves. */
  anonymous?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function readEnvelope<TData>(res: Response): Promise<Envelope<TData>> {
  const text = await res.text();
  if (!text) {
    return { status: res.ok, message: res.ok ? 'ok' : `http_${res.status}` };
  }
  try {
    return JSON.parse(text) as Envelope<TData>;
  } catch {
    // A non-JSON body from this API means something in front of it answered — a
    // proxy, a maintenance page. Report it as such rather than as a parse bug,
    // and never display its text: it has not been through the scrub pipeline.
    throw new ApiError(res.status, 'malformed_response', 'The API returned a response this console could not read.');
  }
}

async function send<TData>(path: string, options: RequestOptions, retry: boolean): Promise<Envelope<TData>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken && !options.anonymous) headers['Authorization'] = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    throw new ApiError(0, 'network_unreachable', 'The API did not answer. Check that the platform binary is running.');
  }

  if (res.status === 401 && !options.anonymous && retry) {
    const refreshed = await refreshSession();
    if (refreshed) return send<TData>(path, options, false);
    accessToken = null;
    onUnauthenticated?.();
    throw new ApiError(401, 'unauthenticated', 'Your session has ended. Sign in again.');
  }

  const envelope = await readEnvelope<TData>(res);

  if (!res.ok || envelope.status === false) {
    throw new ApiError(
      res.status,
      envelope.error ?? `http_${res.status}`,
      envelope.message || 'The request failed.',
      envelope.data,
    );
  }

  return envelope;
}

/** The full envelope, for list endpoints that need `meta`. */
export function requestEnvelope<TData>(path: string, options: RequestOptions = {}): Promise<Envelope<TData>> {
  return send<TData>(path, options, true);
}

/** The `data` payload. Throws ApiError if the server sent a failure envelope. */
export async function request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
  const envelope = await send<TData>(path, options, true);
  return envelope.data as TData;
}

/** A paginated list plus its `meta`. */
export async function requestPage<TItem>(path: string, options: RequestOptions = {}): Promise<Page<TItem>> {
  const envelope = await send<TItem[]>(path, options, true);
  return { items: envelope.data ?? [], meta: envelope.meta };
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { method: 'GET', query }),
  list: <T>(path: string, query?: RequestOptions['query']) => requestPage<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/**
 * Begins Google OAuth for an EXPERT.
 *
 * The role is captured here and stored in the server-side `oauth_states` row;
 * the callback reads it back from that row and never from the request. That is
 * the mechanism that stops a client account reaching this dashboard by editing
 * a URL — and because `users.role` is immutable, such an account is rejected at
 * the callback with `role_mismatch` rather than silently switched.
 */
export function startGoogleAuth(redirectUri: string) {
  return request<{ authorization_url: string; state: string; expires_at: string }>('/auth/google/start', {
    method: 'POST',
    body: { role: 'expert', redirect_uri: redirectUri },
    anonymous: true,
  });
}

export async function logout() {
  try {
    await send('/auth/logout', { method: 'POST', anonymous: true }, false);
  } catch {
    // Logout is idempotent server-side and the expert is leaving either way.
  }
  accessToken = null;
}

/** Restores a session from the refresh cookie on a cold page load. */
export function restoreSession(): Promise<boolean> {
  return refreshSession();
}
